<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import DraftHeader from '@/dashboard/vue/components/layout/DraftHeader/DraftHeader.vue';
import { useDraftStore } from '@/dashboard/store/draftStore';
import DraftFile from '@/dashboard/vue/components/draft/DraftFile.vue';
import { DraftType } from '@/dashboard/types/draft';
import DraftVideo from '@/dashboard/vue/components/draft/DraftVideo.vue';

const route = useRoute();
const draftStore = useDraftStore();
const shortLink = ref<string | undefined>(route.params.short_link as string);

const { draft } = storeToRefs(draftStore);

const hideAll = ref(false);

let retryInterval: number | null = null;

const loadDraftWithRetry = (sl: string) => {
  draftStore.loadDraftByShortLink(sl);

  // Clear any existing interval
  if (retryInterval) {
    clearInterval(retryInterval);
  }

  // Set up retry logic
  retryInterval = setInterval(() => {
    if (!draft.value?.file) {
      draftStore.loadDraftByShortLink(sl);
    } else if (draft.value.draft_size <= 0) {
      draftStore.loadDraftByShortLink(sl);
    } else {
      // Draft file is present, clear the interval
      clearInterval(retryInterval!);
      retryInterval = null;
    }
  }, 10000); // 10 seconds
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    event.preventDefault();
    hideAll.value = !hideAll.value;
  }
};

onMounted(() => {
  setTimeout(() => {
    const sl = route.params.short_link as string | undefined;
    shortLink.value = sl;
    if (sl) {
      loadDraftWithRetry(sl);
    }
  }, 0);

  document.addEventListener('keydown', onKeyDown);
});

// Clean up interval when component is unmounted
onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown);

  if (retryInterval) {
    clearInterval(retryInterval);
    retryInterval = null;
  }
});
</script>

<template>
  <div class="flex h-[var(--app-height)] overflow-hidden">
    <div class="flex flex-1 bg-gray-100 dark:bg-gray-975">
      <draft-header v-if="!hideAll" :draft="draft" :disabled="false" :show-menu-button="true" />

      <draft-file v-if="draft && draft.draft_type === DraftType.file" :draft="draft" />
      <draft-video v-if="draft && draft.draft_type === DraftType.video" :draft="draft" />
    </div>
  </div>
</template>
